import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import InboxesAPI from '../../api/inboxes';
import WebChannel from '../../api/channel/webChannel';
import FBChannel from '../../api/channel/fbChannel';
import TwilioChannel from '../../api/channel/twilioChannel';
import WhatsappChannel from '../../api/channel/whatsappChannel';
import { throwErrorMessage } from '../utils/api';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import camelcaseKeys from 'camelcase-keys';
import { ACCOUNT_EVENTS } from '../../helper/AnalyticsHelper/events';
import { channelActions, buildInboxData } from './inboxes/channelActions';

function collectInboxMessageTemplates(inbox) {
  if (!inbox) return [];

  const channel = inbox.channel || {};
  const attrs = inbox.additional_attributes || inbox.additionalAttributes || {};
  const candidates = [
    inbox.message_templates,
    inbox.messageTemplates,
    attrs.message_templates,
    attrs.messageTemplates,
    channel.message_templates,
    channel.messageTemplates,
  ];

  const found = candidates.find(
    c =>
      (Array.isArray(c) && c.length > 0) ||
      (c &&
        typeof c === 'object' &&
        Array.isArray(c.templates) &&
        c.templates.length > 0)
  );
  if (!found) return [];
  return Array.isArray(found) ? found : found.templates;
}

function resolveWhatsAppTemplateStatus(template) {
  return (
    template.status ??
    template.elementStatus ??
    template.element_status ??
    template.templateStatus ??
    template.template_status
  );
}

function resolveWhatsAppTemplateComponents(template) {
  let c = template.components;
  if (Array.isArray(c) && c.length > 0) {
    return c;
  }
  if (c && typeof c === 'object') {
    const vals = Object.values(c);
    const looksLikeComponents =
      vals.length > 0 &&
      vals.every(
        v => v && typeof v === 'object' && ('type' in v || 'Type' in v)
      );
    if (looksLikeComponents) {
      return vals.map(v => ({
        ...v,
        type: (v.type || v.Type || '').toString(),
        format: v.format || v.Format,
      }));
    }
  }

  const bodyText =
    template.body ||
    template.content ||
    template.text ||
    template.data ||
    template.templateBody;
  if (typeof bodyText === 'string' && bodyText.trim().length > 0) {
    return [{ type: 'BODY', text: bodyText }];
  }

  return [];
}

function isUnsupportedWhatsappTemplateComponent(component) {
  if (!component) return false;
  const type = String(component.type || '').toUpperCase();
  if (
    ['LIST', 'PRODUCT', 'CATALOG', 'CALL_PERMISSION_REQUEST'].includes(type)
  ) {
    return true;
  }
  const format = String(component.format || '').toUpperCase();
  return type === 'HEADER' && format === 'LOCATION';
}

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUpdatingIMAP: false,
    isUpdatingSMTP: false,
    isSyncingTemplates: false,
  },
};

export const getters = {
  getInboxes($state) {
    return $state.records;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getWhatsAppTemplates: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );

    return collectInboxMessageTemplates(inbox);
  },
  getFilteredWhatsAppTemplates: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );

    const templateMediaUrls =
      inbox?.template_media_urls || inbox?.templateMediaUrls || {};
    const templates = collectInboxMessageTemplates(inbox);

    if (!templates || !Array.isArray(templates)) {
      return [];
    }

    return templates
      .filter(template => {
        if (!template) {
          return false;
        }

        const status = resolveWhatsAppTemplateStatus(template);
        const components = resolveWhatsAppTemplateComponents(template);
        if (!status || !components.length) {
          return false;
        }

        if (String(status).toLowerCase() !== 'approved') {
          return false;
        }

        const hasUnsupportedComponents = components.some(component =>
          isUnsupportedWhatsappTemplateComponent(component)
        );

        if (hasUnsupportedComponents) {
          return false;
        }

        return true;
      })
      .map(template => ({
        ...template,
        components: resolveWhatsAppTemplateComponents(template),
        status: resolveWhatsAppTemplateStatus(template),
        gcs_media_url:
          (templateMediaUrls || {})[`${template.name}|${template.language}`] ||
          null,
      }));
  },
  getFilteredTwilioTemplates: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );

    if (!inbox) {
      return [];
    }

    // For Twilio WhatsApp inboxes, templates are stored in content_templates
    // The content_templates is included at the inbox level for Twilio inboxes
    const contentTemplates = inbox.content_templates || {};
    const templates = contentTemplates.templates || [];

    if (!Array.isArray(templates)) {
      return [];
    }

    return templates.filter(template => {
      // Ensure template has required properties
      if (!template || !template.content_sid) {
        return false;
      }

      // Only show approved templates
      if (template.status && template.status.toLowerCase() !== 'approved') {
        return false;
      }

      // For Twilio templates, check if it has the basic required fields
      if (!template.friendly_name && !template.name) {
        return false;
      }

      if (!template.body) {
        return false;
      }

      return true;
    });
  },
  getNewConversationInboxes($state) {
    return $state.records.filter(inbox => {
      const { channel_type: channelType, phone_number: phoneNumber = '' } =
        inbox;

      const isEmailChannel = channelType === INBOX_TYPES.EMAIL;
      const isSmsChannel =
        channelType === INBOX_TYPES.TWILIO &&
        phoneNumber.startsWith('whatsapp');
      return isEmailChannel || isSmsChannel;
    });
  },
  getInbox: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );
    return inbox || {};
  },
  getInboxById: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );
    return camelcaseKeys(inbox || {}, { deep: true });
  },
  getWebsiteInboxes($state) {
    return $state.records.filter(item => item.channel_type === INBOX_TYPES.WEB);
  },
  getTwilioInboxes($state) {
    return $state.records.filter(
      item => item.channel_type === INBOX_TYPES.TWILIO
    );
  },
  getSMSInboxes($state) {
    return $state.records.filter(
      item =>
        item.channel_type === INBOX_TYPES.SMS ||
        (item.channel_type === INBOX_TYPES.TWILIO && item.medium === 'sms')
    );
  },
  getWhatsAppInboxes($state) {
    const twilioWhatsApp = $state.records.filter(
      item =>
        (item.channel_type === INBOX_TYPES.TWILIO &&
          item.medium === 'whatsapp') ||
        item.channel_type === INBOX_TYPES.WHATSAPP ||
        item.channel_type === INBOX_TYPES.GUPSHUP
    );
    return twilioWhatsApp;
  },
  dialogFlowEnabledInboxes($state) {
    return $state.records.filter(
      item => item.channel_type !== INBOX_TYPES.EMAIL
    );
  },
  getFacebookInboxByInstagramId: $state => instagramId => {
    return $state.records.find(
      item =>
        item.instagram_id === instagramId &&
        item.channel_type === INBOX_TYPES.FB
    );
  },
  getInstagramInboxByInstagramId: $state => instagramId => {
    return $state.records.find(
      item =>
        item.instagram_id === instagramId &&
        item.channel_type === INBOX_TYPES.INSTAGRAM
    );
  },
};

const sendAnalyticsEvent = channelType => {
  AnalyticsHelper.track(ACCOUNT_EVENTS.ADDED_AN_INBOX, {
    channelType,
  });
};

export const actions = {
  revalidate: async ({ commit }, { newKey }) => {
    try {
      const isExistingKeyValid = await InboxesAPI.validateCacheKey(newKey);
      if (!isExistingKeyValid) {
        const response = await InboxesAPI.refetchAndCommit(newKey);
        commit(types.default.SET_INBOXES, response.data.payload);
      }
    } catch (error) {
      // Ignore error
    }
  },
  get: async ({ commit }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const response = await InboxesAPI.get(true);
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
      commit(types.default.SET_INBOXES, response.data.payload);
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
    }
  },
  // GET /api/v1/accounts/:account_id/inboxes from network (skips IndexedDB cache)
  fetchFromNetwork: async ({ commit }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const response = await InboxesAPI.get();
      commit(types.default.SET_INBOXES, response.data.payload);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
    }
  },
  createChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      const { channel = {} } = params;
      sendAnalyticsEvent(channel.type);
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      const data = error?.response?.data;
      const errorMessage = data?.message || data?.error;
      const wrapped = new Error(errorMessage);
      wrapped.response = error.response;
      throw wrapped;
    }
  },
  createWebsiteChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(buildInboxData(params));
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('website');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      return throwErrorMessage(error);
    }
  },
  createTwilioChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await TwilioChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('twilio');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw error;
    }
  },
  createFBChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await FBChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('facebook');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw error;
    }
  },
  createWhatsAppEmbeddedSignup: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WhatsappChannel.createEmbeddedSignup(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('whatsapp');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw error;
    }
  },
  ...channelActions,
  // TODO: Extract other create channel methods to separate files to reduce file size
  // - createChannel
  // - createWebsiteChannel
  // - createTwilioChannel
  // - createFBChannel
  updateInbox: async ({ commit }, { id, formData = true, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: true });
    try {
      const response = await InboxesAPI.update(
        id,
        formData ? buildInboxData(inboxParams) : inboxParams
      );
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
      throwErrorMessage(error);
    }
  },
  updateInboxIMAP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
      throwErrorMessage(error);
    }
  },
  updateInboxSMTP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
      throwErrorMessage(error);
    }
  },
  delete: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: true });
    try {
      await InboxesAPI.delete(inboxId);
      commit(types.default.DELETE_INBOXES, inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
      throw error;
    }
  },
  reauthorizeFacebookPage: async ({ commit }, params) => {
    try {
      const response = await FBChannel.reauthorizeFacebookPage(params);
      commit(types.default.EDIT_INBOXES, response.data);
    } catch (error) {
      throw new Error(error.message);
    }
  },
  deleteInboxAvatar: async (_, inboxId) => {
    try {
      await InboxesAPI.deleteInboxAvatar(inboxId);
    } catch (error) {
      throw new Error(error);
    }
  },
  syncTemplates: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isSyncingTemplates: true });
    try {
      // Fetch a fresh baseline so polling waits for the sync triggered below,
      // not for changes that happened before this call (e.g. a previous sync)
      const baselineResponse = await InboxesAPI.get();
      commit(types.default.SET_INBOXES, baselineResponse.data.payload);
      const currentInbox = baselineResponse.data.payload.find(
        inbox => inbox.id === inboxId
      );

      // Use correct field based on channel type
      // WhatsApp Cloud uses message_templates_last_updated
      // Twilio uses content_templates_last_updated
      const getTemplatesLastUpdated = inbox =>
        inbox?.message_templates_last_updated ||
        inbox?.content_templates_last_updated;

      const lastUpdatedBefore = getTemplatesLastUpdated(currentInbox);

      await InboxesAPI.syncTemplates(inboxId);

      const pollInterval = 2000;
      const maxAttempts = 30;
      let attempts = 0;

      const checkForUpdates = async () => {
        const response = await InboxesAPI.get();

        const updatedInbox = response.data.payload.find(
          inbox => inbox.id === inboxId
        );
        const lastUpdatedAfter = getTemplatesLastUpdated(updatedInbox);

        if (lastUpdatedAfter && lastUpdatedAfter !== lastUpdatedBefore) {
          commit(types.default.SET_INBOXES, response.data.payload);
          return true;
        }

        attempts += 1;
        if (attempts >= maxAttempts) {
          throw new Error('Template sync timeout');
        }

        await new Promise(resolve => {
          setTimeout(resolve, pollInterval);
        });
        return checkForUpdates();
      };

      await checkForUpdates();
    } finally {
      commit(types.default.SET_INBOXES_UI_FLAG, { isSyncingTemplates: false });
    }
  },
  deleteTemplate: async (_, { inboxId, templateSid }) => {
    try {
      await InboxesAPI.deleteTemplate(inboxId, templateSid);
    } catch (error) {
      throw new Error(error);
    }
  },
  createTemplate: async (_, { inboxId, templateData, mediaFile }) => {
    try {
      await InboxesAPI.createTemplate(inboxId, templateData, mediaFile);
    } catch (error) {
      throw new Error(error);
    }
  },
  applyForTemplateApproval: async (
    _,
    { inboxId, templateSid, templateName, category }
  ) => {
    try {
      await InboxesAPI.applyForTemplateApproval(
        inboxId,
        templateSid,
        templateName,
        category
      );
    } catch (error) {
      throw new Error(error);
    }
  },

  // WhatsApp Cloud API template operations
  createWhatsappTemplate: async (
    { commit },
    { inboxId, templateData, mediaFile }
  ) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isSyncingTemplates: true });
    try {
      const response = await InboxesAPI.createWhatsappTemplate(
        inboxId,
        templateData,
        mediaFile
      );
      return response.data;
    } finally {
      commit(types.default.SET_INBOXES_UI_FLAG, { isSyncingTemplates: false });
    }
  },

  deleteWhatsappTemplate: async ({ commit }, { inboxId, templateName }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isSyncingTemplates: true });
    try {
      const response = await InboxesAPI.deleteWhatsappTemplate(
        inboxId,
        templateName
      );
      return response.data;
    } finally {
      commit(types.default.SET_INBOXES_UI_FLAG, { isSyncingTemplates: false });
    }
  },
};

export const mutations = {
  [types.default.SET_INBOXES_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  [types.default.SET_INBOXES]: MutationHelpers.set,
  [types.default.SET_INBOXES_ITEM]: MutationHelpers.setSingleRecord,
  [types.default.ADD_INBOXES]: MutationHelpers.create,
  [types.default.EDIT_INBOXES]: MutationHelpers.update,
  [types.default.DELETE_INBOXES]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
